"use client";
import React, { useCallback } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";

// services and utils imports
import { useWidgetClick } from "../../../../utils/dynamicHomeUtils/WidgetClickContext";
import { runOnNavigationIntent } from "../../../../utils/common/navigationIntent";
import { setIsNavigatingAction } from "../../../../store/slices/commonSlice";

/**
 * Renders the section footer with a CTA link.
 * @param {object} props - Component props
 * @param {boolean} props.isMobile - Whether the viewport is mobile
 * @param {object} props.sectionSettings - Section settings object containing footer config
 * @param {string} [props.activeTabLabel] - Optional active tab label to append to CTA text
 * @returns {React.ReactElement|null} The section footer or null
 */
const SectionFooter = ({ sectionSettings, isMobile, activeTabLabel, activeTabLink }) => {
  const dispatch = useDispatch();
  const { onCellClick } = useWidgetClick() ?? {};
  const footerCta = sectionSettings?.footer?.cta;
  const sectionPadding = {
    paddingLeft: `${sectionSettings?.padding?.left || 0}px`,
    paddingRight: `${sectionSettings?.padding?.right || 0}px`,
  };

  /**
   * Tracks Widget Clicked (footer CTA) before navigation.
   * Must run unconditionally — hooks cannot appear after an early return.
   * @param {React.MouseEvent} event - Click event from the CTA anchor.
   * @returns {void}
   */
  const onFooterCtaClick = useCallback(
    (event) => {
      if (!footerCta) return;
      runOnNavigationIntent(event, () => dispatch(setIsNavigatingAction(true)));
      onCellClick?.({
        action: "CTA Clicked",
        item: {
          item_name: footerCta.text || "View All",
          redirection_url: footerCta.linkUrl || undefined,
        },
      });
    },
    [onCellClick, footerCta],
  );

  if (!footerCta) return null;

  const footerCtaStyle = {
    color: footerCta.textColor || "#1A1A1A",
    backgroundColor: footerCta.backgroundColor || "#ffffff",
    borderColor: footerCta.borderColor || "#1A1A1A",
    borderRadius: `${footerCta.borderRadius || 0}px`,
    borderWidth: `${footerCta.borderThickness || 2}px`,
    width: footerCta.width === "full" ? "100%" : footerCta.width || "auto",
    minWidth: "100px",
  };

  return (
    <div
      className={isMobile ? "mt-[20px] flex justify-center" : "mt-[40px] flex justify-center"}
      style={sectionPadding}
    >
      <a
        href={activeTabLink || footerCta.linkUrl || "#"}
        // target="_blank"
        rel="noopener noreferrer"
        aria-label={`${activeTabLabel ? `${footerCta.text || "View All"} ${activeTabLabel}` : footerCta.text || "View All"} - ${sectionSettings?.header?.title || ""}`.trim()}
        className="flex items-center justify-center gap-2 border border-solid px-16 py-8 font-medium transition-colors hover:opacity-80"
        style={{ ...footerCtaStyle, minWidth: "328px" }}
        onClick={onFooterCtaClick}
      >
        <span className="truncate">
          {activeTabLabel ? `${footerCta.text || "View All"} ${activeTabLabel}` : footerCta.text || "View All"}
        </span>
        {footerCta.showArrow && (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M7.5 3.75L13.75 10L7.5 16.25"
              stroke="#191A0B"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </a>
    </div>
  );
};

SectionFooter.propTypes = {
  sectionSettings: PropTypes.object,
  isMobile: PropTypes.bool.isRequired,
  activeTabLabel: PropTypes.string,
  activeTabLink: PropTypes.string,
};

export default SectionFooter;
